import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@components/button';
import { InfoBox } from '@components/info-box';
import { PasswordField } from '@components/password-field';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';

import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';

import { myTimePlanGetSettings, myTimePlanSaveSettings } from './api';
import { MyTimePlanSaveSettingsErrorResponse, MyTimePlanSaveSettingsResponse } from './types';

export const MyTimePlanSettingsPage = () => {
  const [state, setState] = useState({
    url: '',
    username: '',
    password: '',
  });
  const { t } = useStore();
  const settingsQuery = useQuery({
    queryKey: ['myTimePlanSettings'],
    queryFn: myTimePlanGetSettings,
    onSuccess: (data) => {
      setState((old) => ({
        ...old,
        url: data.url,
        username: data.username,
      }));
    },
  });
  const saveSettings = useMutation<
    MyTimePlanSaveSettingsResponse,
    MyTimePlanSaveSettingsErrorResponse
  >(() => myTimePlanSaveSettings(state.url, state.username, state.password), {});
  return (
    <Stack space={4}>
      <Stack space={2}>
        <Text font="heading2" text="MyTimePlan" />
        {settingsQuery.isSuccess && settingsQuery.data.connected && (
          <Tag
            text={t('integrations.mytimeplan.settings.connected')}
            variant="green"
            size="small"
          />
        )}
      </Stack>
      <Separator />
      {(settingsQuery.isLoading || saveSettings.isLoading) && <Loading fixed />}
      {settingsQuery.isSuccess && (
        <Stack space={6}>
          <Text text={t('integrations.mytimeplan.settings.description')} />
          {saveSettings.error && (
            <InfoBox variant="red" text={saveSettings.error.response.data.reason} />
          )}
          {saveSettings.data && <InfoBox variant="green" text={saveSettings.data.reason} />}
          <TextField
            label={t('integrations.mytimeplan.settings.url.label')}
            isDisabled={saveSettings.isLoading}
            onChange={(value) => setState((old) => ({ ...old, url: value }))}
            defaultValue={state.url}
          />
          <TextField
            label={t('integrations.mytimeplan.settings.username.label')}
            isDisabled={saveSettings.isLoading}
            onChange={(value) => setState((old) => ({ ...old, username: value }))}
            defaultValue={state.username}
          />
          <PasswordField
            label={t('integrations.mytimeplan.settings.password.label')}
            isDisabled={saveSettings.isLoading}
            onChange={(value) => setState((old) => ({ ...old, password: value }))}
          />
          <Stack space={0} orientation="horizontal" justify={'flex-end'}>
            <Button
              text={t('integrations.mytimeplan.settings.submit.label')}
              isDisabled={
                Object.values(state).some((value) => value === '') || saveSettings.isLoading
              }
              onClick={() => saveSettings.mutate()}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
